import { Button } from '@design/components/button/Button';
import { ThreeDots } from '@design/components/three-dots/ThreeDots';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { WebOnlyFeat } from '@terminal/config/WebOnlyFeat';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useEntity } from '@terminal/react-query-hooks/entities/useEntities';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { AssetOverviewWidget } from '@terminal/widgets/asset-overview-widget/AssetOverviewWidget';
import { useTranslation } from 'react-i18next';

import styles from './AssetOverviewPreviewCard.module.scss';

const extractTag = (value: string): string => {
  const params = new URLSearchParams(value.split('?')[1] ?? '');
  return params.get('asset') ?? value;
};

export interface AssetOverviewPreviewCardProps {
  entityPath: string;
}

export const AssetOverviewPreviewCard = ({
  entityPath,
}: AssetOverviewPreviewCardProps) => {
  const { t } = useTranslation(['chat', 'widget']);

  const entityTag = extractTag(entityPath);
  const { data, isError, isLoading } = useEntity(entityTag);
  const { navigateToPage } = useNavigateToAssetOverview();

  const entity = data?.data;

  if (isLoading) {
    return (
      <div
        className={styles.container}
        data-testid="asset-overview-preview-card-loading"
      >
        <div className={styles.stateContainer}>
          <ThreeDots />
        </div>
      </div>
    );
  }

  if (isError || !entity) {
    return (
      <div
        className={styles.container}
        data-testid="asset-overview-preview-card-error"
      >
        <div className={styles.stateContainer}>
          <p className={styles.emptyText}>{t('chat:empty')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container} data-testid="asset-overview-preview-card">
      <WebOnlyFeat>
        <div className={styles.header}>
          <Button
            iconName="Expand"
            label={t('widget:assetOverview.assetOverview')}
            onClick={() =>
              navigateToPage({
                entityTag: entity.tag,
                source: POSTHOG_TRACKING_SOURCES.ALFRED_ASSET_PREVIEW,
              })
            }
            variant="empty"
            size="xsmall"
            data-testid="asset-overview-preview-card-expand"
          />
        </div>
      </WebOnlyFeat>
      <div className={styles.body}>
        <ErrorBoundaryWrapper>
          <AssetOverviewWidget
            entity={entity}
            isDrawer
            source={POSTHOG_TRACKING_SOURCES.ALFRED_ASSET_PREVIEW}
          />
        </ErrorBoundaryWrapper>
      </div>
    </div>
  );
};
